import { memo, useCallback, useEffect, useRef, useState } from "react";

import { Check, ChevronsUpDown, Loader2, X } from "lucide-react";

import { useApi } from "../../context/ApiContext";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button.jsx";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command.jsx";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover.jsx";

const LazySearchSelect = ({
  value,
  onChange,
  placeholder = "Select an item...",
  searchPlaceholder = "Search items...",
  apiUrl, // e.g., "/projects"
  dataKey = "items", // e.g., "projects" or "items"
  labelKey = "name",
  valueKey = "id",
  pageSize = 20,
  showAllOption = false,
  allOptionLabel = "All",
  renderItem = null,
  popoverWidth = "w-[--radix-popover-trigger-width]",
  icon: Icon = null,
  prependOptions = [], // e.g., [{ id: 'system', name: 'System (Global)' }]
  disableInitialFetch = false,
  valueFetchUrl = null, // Custom URL to fetch a single item by value
  isClearable = false, // Allow clearing the selection
  allOptionValue = "all", // The value used for the 'All' option
  className = "",
  extraParams = {} // Additional parameters for the API call
}) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const api = useApi();
  const lastFetchedSearch = useRef(null);
  const isFetching = useRef(false);
  const observer = useRef();

  const fetchItems = useCallback(
    async (isNewSearch = false) => {
      if (isFetching.current) return;

      isFetching.current = true;
      setIsLoading(true);

      try {
        const targetPage = isNewSearch ? 0 : page;

        const response = await api.get(apiUrl, {
          params: {
            page: targetPage,
            size: pageSize,
            search: searchTerm?.trim() || undefined,
            showDeleted: false,
            ...extraParams
          }
        });

        const data = response.data;
        let newItems = [];

        if (Array.isArray(data)) {
          newItems = data;
        } else {
          newItems = data[dataKey] || data.items || [];
        }

        if (isNewSearch) {
          setItems(newItems);
          setPage(1);
          lastFetchedSearch.current = searchTerm;
        } else {
          setItems((prev) => [...prev, ...newItems]);
          setPage((prev) => prev + 1);
        }

        setTotalPages(data.totalPages || 0);
      } catch (error) {
        console.error("Failed to fetch items in LazySearchSelect:", error);
      } finally {
        setIsLoading(false);
        isFetching.current = false;
      }
    },
    [api, apiUrl, dataKey, page, pageSize, searchTerm, JSON.stringify(extraParams)]
  );

  // Initial load and search load
  useEffect(() => {
    if (open) {
      // Skip fetching if we already have items for this search term
      if (items.length > 0 && searchTerm === lastFetchedSearch.current) {
        return;
      }

      const delayDebounceFn = setTimeout(() => {
        fetchItems(true);
      }, 300);

      return () => clearTimeout(delayDebounceFn);
    }
  }, [searchTerm, open, items.length]);
  useEffect(() => {
    if (open) {
      setItems([]);
      setPage(0);
      fetchItems(true);
    }
  }, [JSON.stringify(extraParams)]);
  // Effect to fetch initial value if it's not in the list
  useEffect(() => {
    const fetchInitialValue = async () => {
      // Don't fetch if value is empty or the special "all" value
      if (!value || String(value) === String(allOptionValue)) return;

      // Check if it's already in the items list
      const alreadyLoaded = items.find((item) => String(item[valueKey]) === String(value));
      if (alreadyLoaded) return;

      // Check if it's in prependOptions
      const inPrepend = prependOptions.find((opt) => String(opt[valueKey]) === String(value));
      if (inPrepend) return;

      setIsLoading(true);
      try {
        const fetchUrl = valueFetchUrl || `${apiUrl}/${value}`;
        const response = await api.get(fetchUrl);

        if (response.data) {
          const item = Array.isArray(response.data) ? response.data[0] : response.data;

          if (item && item[valueKey]) {
            setItems((prev) => {
              // Final double check to avoid duplicates
              if (prev.find((p) => String(p[valueKey]) === String(item[valueKey]))) {
                return prev;
              }
              return [item, ...prev];
            });

            onChange(item[valueKey], item);
          }
        }
      } catch (error) {
        console.error("Failed to fetch initial value for", value, ":", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialValue();
  }, [value, apiUrl, valueKey, api, allOptionValue, valueFetchUrl]);

  useEffect(() => {
    return () => {
      if (observer.current) observer.current.disconnect();
    };
  }, []);

  const lastItemRef = useCallback(
    (node) => {
      if (isFetching.current) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && page < totalPages) {
          // Use setTimeout instead of requestAnimationFrame for better stability with React state
          requestAnimationFrame(() => {
            if (!isFetching.current) fetchItems(false);
          });
        }
      });

      if (node) observer.current.observe(node);
    },
    [isLoading, page, totalPages, fetchItems]
  );

  const selectedItem =
    items.find((item) => String(item[valueKey]) === String(value)) ||
    prependOptions.find((opt) => String(opt[valueKey]) === String(value));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "mobile-text flex min-w-0 w-full max-w-full justify-between font-medium bg-white dark:bg-yale-blue-800/40 border-slate-200 dark:border-yale-blue-700 hover:bg-slate-50 dark:hover:bg-yale-blue-800/60 transition-all duration-300 rounded-lg h-10 px-4 group overflow-hidden",
            className
          )}
        >
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {Icon && (
              <div className="p-1 rounded bg-slate-100 dark:bg-yale-blue-900/50 group-hover:bg-white dark:group-hover:bg-yale-blue-800 transition-colors shrink-0">
                <Icon className="h-[18px] w-[18px] text-yale-blue-600 dark:text-yale-blue-400" />
              </div>
            )}
            <span className="truncate block flex-1 text-left">
              {showAllOption && value === allOptionValue ? allOptionLabel : selectedItem ? (selectedItem[labelKey] || selectedItem[valueKey]) : placeholder}
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {isClearable && value && (
              <div
                role="button"
                className="p-1 hover:bg-slate-200 dark:hover:bg-yale-blue-700 rounded-full transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange("", null);
                }}
              >
                <X className="h-3.5 w-3.5 opacity-60 hover:opacity-100" />
              </div>
            )}
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-40 group-hover:opacity-100 transition-opacity" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(popoverWidth, "p-0 !outline-none !ring-0 !shadow-lg border-none")}
        align="start"
        onWheelCapture={(e) => e.stopPropagation()}
        onTouchStartCapture={(e) => e.stopPropagation()}
      >
        <Command
          shouldFilter={false}
          className="!border-none !outline-none !ring-0 [&_[cmdk-input-wrapper]]:bg-slate-50/80 dark:[&_[cmdk-input-wrapper]]:bg-slate-900/50 [&_[cmdk-input-wrapper]]:border-b [&_[cmdk-input-wrapper]]:border-slate-200 dark:[&_[cmdk-input-wrapper]]:border-slate-800"
        >
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchTerm}
            onValueChange={setSearchTerm}
            className="mobile-text h-11 !border-none !outline-none !ring-0 !ring-offset-0 !shadow-none focus:!outline-none focus:!ring-0 focus-visible:!ring-0 focus-visible:!outline-none"
          />
          <CommandList
            className="max-h-[300px] overflow-y-auto overscroll-contain !pointer-events-auto scrollbar-thin"
            onWheelCapture={(e) => e.stopPropagation()}
            onTouchStartCapture={(e) => e.stopPropagation()}
          >
            <CommandEmpty className="p-4 text-center">
              {isLoading ? (
                <div className="flex flex-col items-center gap-2 py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  <span className="text-xs text-slate-500">Loading items...</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 py-4">
                  <span className="text-sm text-slate-500">No items found.</span>
                  {!searchTerm && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs text-primary hover:text-primary h-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        fetchItems(true);
                      }}
                    >
                      Try Refreshing
                    </Button>
                  )}
                </div>
              )}
            </CommandEmpty>
            <CommandGroup>
              {showAllOption && !searchTerm && (
                <CommandItem
                  value={allOptionValue}
                  onSelect={() => {
                    onChange(allOptionValue, {
                      [valueKey]: allOptionValue,
                      [labelKey]: allOptionLabel
                    });
                    setOpen(false);
                  }}
                  className="mobile-text h-11"
                >
                  <Check className={cn("mr-2 h-4 w-4", value === allOptionValue ? "opacity-100" : "opacity-0")} />
                  {allOptionLabel}
                </CommandItem>
              )}
              {prependOptions.map((option, index) => (
                <CommandItem
                  key={`prepend-${option[valueKey]}-${index}`}
                  value={option[valueKey]}
                  onSelect={() => {
                    onChange(option[valueKey], option);
                    setOpen(false);
                  }}
                  className="mobile-text"
                >
                  <Check className={cn("mr-2 h-4 w-4 shrink-0", value === option[valueKey] ? "opacity-100" : "opacity-0")} />
                  <span className="truncate block flex-1">{option[labelKey]}</span>
                </CommandItem>
              ))}
              {items.map((item, index) => (
                <CommandItem
                  key={`${item[valueKey]}-${index}`}
                  value={String(item[valueKey])}
                  onSelect={() => {
                    onChange(item[valueKey], item);
                    setOpen(false);
                  }}
                  className="mobile-text"
                >
                  <div ref={index === items.length - 1 ? lastItemRef : null} className="flex items-center w-full min-w-0">
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 shrink-0",
                        String(value) === String(item[valueKey]) ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {renderItem ? renderItem(item) : <span className="truncate block flex-1">{item[labelKey] || item[valueKey]}</span>}
                  </div>
                </CommandItem>
              ))}
              {isLoading && page > 0 && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin text-yale-blue-500" />
                </div>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default memo(LazySearchSelect);
